/**
 * OccupationalRisk List Page
 * List and manage occupational risks
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  GroupWork as ApplicationIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  HealthAndSafety as HealthIcon,
  PlaylistAddCheck as BulkIcon,
} from '@mui/icons-material';
import {
  ActionButton,
  PageShell,
  DataTable,
  type DataTableColumn,
  AlertBadge,
  generateApplicationAlerts,
  DeleteWithDependenciesDialog,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle, useVeredito } from '../../authorization/useVeredito';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import { OccupationalRisk, OccupationalRiskDependencies, OccupationalRiskFilters } from '../../types/occupationalRisk';
import { dependencyItems } from '../../utils/dependencyItems';
import { RiskTypeChip } from './components/RiskTypeChip';
import { OccupationalRiskFiltersComponent, RiskFiltersData } from './components/OccupationalRiskFilters';
import OccupationalRiskImportDialog from './components/OccupationalRiskImportDialog';
import { PainelCodigoEsocial } from './components/PainelCodigoEsocial';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';

const getRiskAlerts = (risk: OccupationalRisk) => {
  if (!risk._counts) return [];
  return generateApplicationAlerts({
    companyCount: risk._counts.companies,
    departmentCount: risk._counts.departments,
    occupationCount: risk._counts.occupations,
  });
};

const columns: DataTableColumn<OccupationalRisk>[] = [
  {
    id: 'riskFactorName',
    label: 'Nome do Fator',
    primary: true,
    render: (risk) => (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" fontWeight="medium">
          {risk.riskFactorName}
        </Typography>
        <AlertBadge alerts={getRiskAlerts(risk)} />
      </Box>
    ),
  },
  {
    id: 'hazard',
    label: 'Perigo ou Fator de Risco',
    render: (risk) => risk.hazard,
  },
  {
    id: 'riskType',
    label: 'Tipo',
    render: (risk) => <RiskTypeChip type={risk.riskType} />,
  },
  {
    id: 'classification',
    label: 'Classificação',
    render: (risk) => (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {risk.hazardousAgent && (
          <Tooltip title="Agente Perigoso (NR-16)">
            <Chip
              icon={<WarningIcon />}
              label="NR-16"
              size="small"
              color="error"
              variant="outlined"
            />
          </Tooltip>
        )}
        {risk.unhealthyAgent && (
          <Tooltip title="Agente Insalubre (NR-15)">
            <Chip
              icon={<HealthIcon />}
              label="NR-15"
              size="small"
              color="warning"
              variant="outlined"
            />
          </Tooltip>
        )}
        {risk.socialSecurityAgent && (
          <Tooltip title="Agente Previdenciário">
            <Chip
              label="Previd."
              size="small"
              color="info"
              variant="outlined"
            />
          </Tooltip>
        )}
        {!risk.hazardousAgent && !risk.unhealthyAgent && !risk.socialSecurityAgent && (
          <Typography variant="caption" color="text.secondary">
            -
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'esocialAgentCode',
    label: 'Código eSocial',
    render: (risk) => {
      if (risk.esocialAgentCodeNotApplicable) {
        return <Chip label="Não se aplica" size="small" variant="outlined" />;
      }
      if (risk.esocialAgentCode) return risk.esocialAgentCode;
      return (
        <Typography variant="caption" color="text.secondary">
          —
        </Typography>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    render: (risk) => (
      <Chip
        label={risk.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
        size="small"
        color={risk.status === 'ACTIVE' ? 'success' : 'default'}
      />
    ),
  },
];

export const OccupationalRiskListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/occupational-risks/:id/edit', pode('occupational_risk.update'), 'Editar');
  const visualizar = useControle('/occupational-risks/:id', pode('occupational_risk.read'), 'Visualizar');
  const excluir = useAcao('occupational_risk.delete', 'Excluir');
  const [risks, setRisks] = useState<OccupationalRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<OccupationalRiskFilters>({});
  const [selecionados, setSelecionados] = useState<(string | number)[]>([]);
  const [painelAberto, setPainelAberto] = useState(false);

  /**
   * #473 — a gravação em lote é escrita em catálogo compartilhado, e a regra V10 do backend
   * a recusa fora do contexto Plataforma. Oferecer aqui o que a rota recusa lá já deu
   * estrago antes, então a seleção múltipla nem aparece nos outros contextos.
   */
  const loteDeCodigos = useVeredito(
    '/occupational-risks',
    pode('occupational_risk.update', { perspectiva: 'plataforma' })
  );

  // O id chega como string do backend, embora o tipo diga `number`.
  const riscosSelecionados = useMemo(
    () => risks.filter((risk) => selecionados.some((id) => String(id) === String(risk.id))),
    [risks, selecionados]
  );

  const loadRisks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await occupationalRiskService.getOccupationalRisks(params);
      setRisks(response.risks);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar riscos ocupacionais');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  const handleFilter = useCallback((data: RiskFiltersData) => {
    const filters: OccupationalRiskFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.status) filters.status = data.status as OccupationalRiskFilters['status'];
    if (data.riskType) filters.riskType = data.riskType as OccupationalRiskFilters['riskType'];
    if (data.unhealthyAgent) filters.unhealthyAgent = true;
    if (data.hazardousAgent) filters.hazardousAgent = true;
    if (data.socialSecurityAgent) filters.socialSecurityAgent = true;
    if (data.semCodigoTabela24) filters.esocialAgentCode = false;

    setApiFilters(filters);
    setPage(0);
    // A seleção é de linhas desta página: com outro filtro, ela deixa de existir na tela.
    setSelecionados([]);
  }, []);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<OccupationalRisk, OccupationalRiskDependencies>({
      checkDependencies: (id) => occupationalRiskService.checkDependencies(id),
      deleteEntity: (id) => occupationalRiskService.deleteOccupationalRisk(id),
      checkErrorMessage: 'Erro ao verificar vínculos do risco ocupacional',
      deleteErrorMessage: 'Erro ao excluir risco ocupacional',
      onError: setError,
      onDeleted: loadRisks,
    });

  const handleView = (risk: OccupationalRisk) => {
    navigate(`/occupational-risks/${risk.id}`);
  };

  const handleEdit = (risk: OccupationalRisk) => {
    navigate(`/occupational-risks/${risk.id}/edit`);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await occupationalRiskService.exportRisks();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'riscos-ocupacionais.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao exportar riscos ocupacionais');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Riscos Ocupacionais' },
      ]}
      title="Gestão de Riscos Ocupacionais"
      subtitle="Gerencie os riscos ocupacionais (NR-09, NR-15, NR-16)"
      actions={
        <>
          <ActionButton
            acao="occupational_risk.read"
            rotulo="Exportar"
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exportLoading}
          >
            Exportar
          </ActionButton>
          <ActionButton
            acao="occupational_risk.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/risk-applications"
            exige={pode('risk_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="secondary"
            startIcon={<ApplicationIcon />}
          >
            Aplicar Riscos
          </RouteActionButton>
          <RouteActionButton
            para="/occupational-risks/new"
            exige={pode('occupational_risk.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Risco
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <OccupationalRiskFiltersComponent onFilter={handleFilter} loading={loading} total={total} />
      </Box>

      <DataTable
        columns={columns}
        rows={risks}
        getRowId={(risk) => risk.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum risco ocupacional encontrado',
          description:
            'Riscos ocupacionais são cadastrados nesta tela e depois aplicados a empresas, setores, funções e funcionários. Os riscos aplicados entram no PGR, no LTCAT e nos laudos.',
        }}
        selection={
          loteDeCodigos.liberado
            ? {
                selected: selecionados,
                onChange: setSelecionados,
                actions: (
                  <Button
                    size="small"
                    startIcon={<BulkIcon />}
                    onClick={() => setPainelAberto(true)}
                  >
                    Concluir código eSocial
                  </Button>
                ),
              }
            : undefined
        }
        actions={[
          {
            icon: <VisibilityIcon fontSize="small" />,
            onClick: handleView,
            ...visualizar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: (value) => {
            setPage(value);
            setSelecionados([]);
          },
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
            setSelecionados([]);
          },
        }}
      />

      <PainelCodigoEsocial
        aberto={painelAberto}
        riscos={riscosSelecionados}
        onFechar={() => setPainelAberto(false)}
        onGravado={() => {
          setPainelAberto(false);
          setSelecionados([]);
          loadRisks();
        }}
      />

      <OccupationalRiskImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={loadRisks}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasDependencies)}
        title="Excluir risco ocupacional"
        confirmText="Tem certeza que deseja excluir este risco ocupacional?"
        prerequisite={{
          title: 'Este risco está em uso',
          reason: 'Riscos com aplicações ativas não podem ser excluídos — remova as aplicações primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Ver aplicações',
            permission: 'occupational_risk.update',
            mode: 'navigate',
            route: `/risk-applications?riskId=${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para remover as aplicações deste risco antes de excluí-lo.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default OccupationalRiskListPage;
